import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { Input } from '../ui/input';
import { 
  User, 
  Phone, 
  Search, 
  Loader2, 
  CheckCircle2, 
  UserPlus, 
  X, 
  IndianRupee, 
  Calendar 
} from 'lucide-react';

export interface ServiceCustomerItem {
  id: string;
  customer_name: string;
  phone?: string | null;
  address?: string | null;
  bookings_count?: number;
  last_booking_at?: string | null;
  advance_held?: number;
  due_amount?: number;
}

interface ServiceCustomerSearchDropdownProps {
  tenantId: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  selectedCustomerId: string | null;
  onSelectCustomer: (customer: ServiceCustomerItem) => void;
  onChangeName: (val: string) => void;
  onChangePhone: (val: string) => void;
  onChangeAddress?: (val: string) => void;
  onClearCustomer: () => void;
}

export function ServiceCustomerSearchDropdown({
  tenantId,
  customerName,
  customerPhone,
  customerAddress,
  selectedCustomerId,
  onSelectCustomer,
  onChangeName,
  onChangePhone,
  onChangeAddress,
  onClearCustomer
}: ServiceCustomerSearchDropdownProps) {
  const supabase = getSupabaseClient();
  const [results, setResults] = useState<ServiceCustomerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeInput, setActiveInput] = useState<'name' | 'phone' | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<any>(null);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveInput(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const searchCustomers = useCallback(async (query: string) => {
    if (!supabase || !tenantId || !query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('search_service_customers', {
        p_tenant_id: tenantId,
        p_query: query.trim(),
        p_limit: 8
      });

      if (error) {
        console.warn('Customer search warning:', error);
        setResults([]);
      } else {
        setResults((data as ServiceCustomerItem[]) || []);
      }
    } catch (err) {
      console.warn('Failed to search customers:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, tenantId]);

  const handleInputChange = (type: 'name' | 'phone', value: string) => {
    if (type === 'name') {
      onChangeName(value);
    } else {
      onChangePhone(value);
    }

    // Reset customerId on manual edit
    if (selectedCustomerId) {
      onClearCustomer();
    }

    setActiveInput(type);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!value.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    setLoading(true);
    setHighlightedIndex(-1);

    debounceTimerRef.current = setTimeout(() => {
      searchCustomers(value);
    }, 250);
  };

  const handleSelect = (customer: ServiceCustomerItem) => {
    onSelectCustomer(customer);
    setIsOpen(false);
    setActiveInput(null);
  };

  const handleCreateNewCustomer = () => {
    onClearCustomer();
    setIsOpen(false);
    setActiveInput(null);
  };

  const currentQuery = activeInput === 'name' ? customerName : customerPhone;

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < results.length) {
        handleSelect(results[highlightedIndex]);
      } else if (results.length === 0 && currentQuery.trim()) {
        handleCreateNewCustomer();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative space-y-3">
      {/* Selected Customer Chip / Indicator */}
      {selectedCustomerId && (
        <div className="flex items-center justify-between p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-xs animate-in fade-in duration-150">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={15} className="text-primary shrink-0" />
            <div>
              <span className="font-bold text-slate-800 dark:text-slate-100">
                Linked Customer: {customerName}
              </span>
              {customerPhone && (
                <span className="text-slate-500 dark:text-slate-400 ml-1.5 font-mono text-[11px]">
                  ({customerPhone})
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClearCustomer}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X size={12} />
            <span>Clear / Naya Customer</span>
          </button>
        </div>
      )}

      {/* Name and Phone Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Customer Name */}
        <div>
          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
            Customer Name <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              type="text"
              placeholder="e.g. Ramesh Kumar"
              value={customerName}
              onChange={e => handleInputChange('name', e.target.value)}
              onFocus={() => {
                if (customerName.trim() && !selectedCustomerId) {
                  setActiveInput('name');
                  setIsOpen(true);
                  searchCustomers(customerName);
                }
              }}
              onKeyDown={handleKeyDown}
              required
              className="pl-9 h-10 text-xs"
            />
            {loading && activeInput === 'name' && (
              <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary animate-spin" />
            )}
          </div>
        </div>

        {/* Customer Phone */}
        <div>
          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
            Phone Number (10 digits)
          </label>
          <div className="relative">
            <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              type="tel"
              placeholder="e.g. 9876543210"
              value={customerPhone}
              onChange={e => handleInputChange('phone', e.target.value)}
              onFocus={() => {
                if (customerPhone.trim() && !selectedCustomerId) {
                  setActiveInput('phone');
                  setIsOpen(true);
                  searchCustomers(customerPhone);
                }
              }}
              onKeyDown={handleKeyDown}
              className="pl-9 h-10 text-xs font-mono"
            />
            {loading && activeInput === 'phone' && (
              <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary animate-spin" />
            )}
          </div>
        </div>
      </div>

      {/* Helpful Hint */}
      <p className="text-[11px] text-slate-500 dark:text-slate-400 italic flex items-center gap-1">
        <span>💡</span>
        <span>Phone dene par customer ka account/khata ban jata hai.</span>
      </p>

      {/* Search Dropdown Popover */}
      {isOpen && currentQuery.trim().length > 0 && !selectedCustomerId && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto animate-in fade-in duration-100">
          {loading && results.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin text-primary" />
              <span>Searching customer records...</span>
            </div>
          ) : results.length > 0 ? (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="px-3 py-1.5 bg-slate-50 dark:bg-slate-800/60 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Matched Existing Customers ({results.length})
              </div>
              {results.map((c, idx) => {
                const isSelected = highlightedIndex === idx;
                const adv = Number(c.advance_held || 0);
                const due = Number(c.due_amount || 0);

                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleSelect(c)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`w-full text-left p-3 text-xs flex items-center justify-between transition-colors ${
                      isSelected
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/80 text-slate-800 dark:text-slate-200'
                    }`}
                  >
                    <div className="space-y-0.5">
                      <div className="font-bold flex items-center gap-2">
                        <span>{c.customer_name}</span>
                        {c.phone && (
                          <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 font-normal">
                            • {c.phone}
                          </span>
                        )}
                      </div>
                      {c.address && (
                        <p className="text-[11px] text-slate-400 truncate max-w-sm">
                          {c.address}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 text-right">
                      {adv > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                          Advance ₹{adv}
                        </span>
                      )}
                      {due > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300">
                          Baaki ₹{due}
                        </span>
                      )}
                      {c.bookings_count !== undefined && c.bookings_count > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          {c.bookings_count} bookings
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* Option to create brand new customer account */}
              <button
                type="button"
                onClick={handleCreateNewCustomer}
                className="w-full text-left p-3 text-xs flex items-center gap-2 text-primary hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors font-semibold"
              >
                <UserPlus size={14} />
                <span>Naya customer: "{currentQuery}" (Backend naya account banayega)</span>
              </button>
            </div>
          ) : (
            <div className="p-3 text-xs">
              <button
                type="button"
                onClick={handleCreateNewCustomer}
                className="w-full text-left p-2 rounded-lg bg-primary/5 hover:bg-primary/10 text-primary flex items-center gap-2 font-medium transition-colors"
              >
                <UserPlus size={14} />
                <span>Naya customer: "{currentQuery}" (Naya account banega)</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
export default ServiceCustomerSearchDropdown;
